import React, {forwardRef} from 'react';

import {Icons} from '@pb/kit-component-icon';
import {withCssModule} from '@pb/kit-util-with-css-module';

import {Avatar} from '../../avatar';
import styles from '../../avatar.module.styl';
import {CURRENT_USER_COLOR, INITIAL_COLORS} from '../../constants';
import {getInitials, prepareName} from '../../utils';

import type {AvatarProps} from '../../avatar';
import type {Name} from '../../typedef';


const classNames = withCssModule(styles);

/**
 * Extended `Avatar` props that add automatic initials generation and color
 * assignment on top of the base avatar functionality.
 *
 * When `name` is provided and no `src` is given, `AvatarWrapped` derives
 * initials from the name and applies a background color from the design system's
 * palette. This removes the need to pre-compute initials or colors in the
 * consuming component.
 */
export interface AvatarWrappedProps extends AvatarProps {
    /**
     * Name used to derive initials when no image is available. Accepts either a
     * string (displayed as-is) or a `Name` object `{ firstName?, lastName? }`.
     * When both `name` and `src` are provided, `src` takes precedence and the
     * initials are not shown.
     */
    name?: Name | string,
    /**
     * Index into the design system's avatar color palette used to assign a
     * consistent background color to the initials placeholder. The index wraps
     * around modulo the palette length, so any non-negative integer is valid.
     * When omitted or when `isCurrentUser` is `true`, the "current user" color
     * (blue) is applied instead.
     */
    colorIndex?: number,
    /**
     * When `true`, applies the "current user" color (blue) to the initials
     * background regardless of `colorIndex`. Use this to visually distinguish the
     * logged-in user's avatar from other avatars in a list.
     */
    isCurrentUser?: boolean,
}

export const AvatarWrapped = forwardRef<HTMLElement, AvatarWrappedProps>(({
    name,
    colorIndex,
    isCurrentUser,
    ...props
}, ref) => {
    let {placeholder = ''} = props;
    let names;
    let avatarClassName: string | undefined = props.className ?? '';

    if (name && !props.src) {
        names = prepareName(name);
        if (names) {
            const availableColorLength = INITIAL_COLORS.length;
            placeholder = <span aria-hidden="true">{getInitials(names)}</span>;

            const color = isCurrentUser || colorIndex === undefined
                ? CURRENT_USER_COLOR
                : INITIAL_COLORS[colorIndex % availableColorLength];

            avatarClassName = classNames(avatarClassName, `avatar__initials avatar__initials_color-${color}`);
        }
    }

    if (placeholder === Icons.PERSON_ROUNDED && !names) {
        avatarClassName = classNames(avatarClassName, 'avatar__person');
    }

    return <Avatar {...{...props, placeholder, className: avatarClassName}} ref={ref}/>;
});

AvatarWrapped.displayName = 'AvatarWrapped';
